import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { paths } from "~/router/paths";
import {
  IAccount,
  IActiveSubscription,
  PlanStatus,
  SubscriptionAction,
} from "~/types/account";

export type SubscriptionLockReason =
  | "past_due"
  | "trial_ended"
  | "cancelled"
  | "expired";

const EXEMPT_PATH_PREFIXES = [
  paths.checkout.index,
  paths.settings.plans,
  paths.settings.account,
  paths.settings.membership,
];

// Locked until a payment actually goes through. Saving a new card used to lift the
// lock straight away whether or not the card worked; the card update now charges
// the past-due invoice and the subscription leaves PAST_DUE once it is paid.
export const isPastDueSubscription = (account: IAccount): boolean =>
  account.last_subscription?.status === PlanStatus.PAST_DUE;

const isTrialEndedSubscription = (
  account: IAccount,
  subscription: IActiveSubscription
): boolean => {
  if (!subscription.trial_end_at) {
    return false;
  }

  const trialEnd = new Date(subscription.trial_end_at);
  if (trialEnd > new Date()) {
    return false;
  }

  if (account.last_subscription_action === SubscriptionAction.TRIAL_CANCELLED) {
    return true;
  }

  if (subscription.cancelled_at) {
    const cancelledAt = new Date(subscription.cancelled_at);
    if (cancelledAt <= trialEnd) {
      return true;
    }

    const daysAfterTrial =
      (cancelledAt.getTime() - trialEnd.getTime()) / (1000 * 60 * 60 * 24);

    return (
      account.last_subscription_action === SubscriptionAction.CANCELLED &&
      daysAfterTrial < 1
    );
  }

  return true;
};

const isCancelledSubscription = (
  account: IAccount,
  subscription: IActiveSubscription
): boolean => {
  if (isTrialEndedSubscription(account, subscription)) {
    return false;
  }

  return account.last_subscription_action === SubscriptionAction.CANCELLED;
};

export const getSubscriptionLockReason = (
  account: IAccount | null
): SubscriptionLockReason | null => {
  if (DISABLE_PAYMENTS || !account) {
    return null;
  }

  if (isPastDueSubscription(account)) {
    return "past_due";
  }

  if (account.active_subscription) {
    return null;
  }

  const subscription = account.last_subscription;
  if (!subscription || subscription.status !== PlanStatus.INACTIVE) {
    return null;
  }

  if (isTrialEndedSubscription(account, subscription)) {
    return "trial_ended";
  }

  if (isCancelledSubscription(account, subscription)) {
    return "cancelled";
  }

  return "expired";
};

export const isSubscriptionExpiredExemptPath = (pathname: string): boolean => {
  return EXEMPT_PATH_PREFIXES.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
};

export const shouldShowSubscriptionLockModal = (
  account: IAccount | null,
  pathname: string,
  search: string = ""
): boolean => {
  if (isSubscriptionExpiredExemptPath(pathname)) {
    return false;
  }

  if (new URLSearchParams(search).has("plan_id")) {
    return false;
  }

  return getSubscriptionLockReason(account) !== null;
};

/** @deprecated Use shouldShowSubscriptionLockModal */
export const shouldShowSubscriptionExpiredModal =
  shouldShowSubscriptionLockModal;

/** @deprecated Use getSubscriptionLockReason */
export const isSubscriptionExpired = (account: IAccount | null): boolean =>
  getSubscriptionLockReason(account) === "expired";
